import styled from 'styled-components';

interface LearningPlanItem {
  type: 'module' | 'project';
  slug: string;
  title?: string;
  iconUrl?: string;
}

interface LearningRecommendationsCardProps {
  items: LearningPlanItem[];
}

const CardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  padding-bottom: 16px;
`;

const PlanItemBox = styled.div<{ type: 'module' | 'project' }>`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  background-color: ${({ theme }) => theme.colors.bgDefaultSecondary};
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease-in-out;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    background-color: ${({ theme }) => theme.colors.bgDefault};
  }
`;

const ItemIcon = styled.div<{ type: 'module' | 'project' }>`
  width: 25px;
  height: 25px;
  border-radius: 6px;
  margin-right: 12px;
  flex-shrink: 0;
  background-color: ${({ type, theme }) =>
    type === 'module' ? theme.colors.buttonPrimary : theme.colors.highlight}20;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 15px;
    height: 15px;
    object-fit: contain;
    margin-top: 0;
    padding-bottom: 0;
  }

  i {
    color: ${({ type, theme }) =>
      type === 'module' ? theme.colors.buttonPrimary : theme.colors.highlight};
    font-size: 14px;
  }
`;

const ItemTitle = styled.div`
  flex: 1;
  font-weight: 400;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textActive};
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const ItemCount = styled.div<{ type: 'module' | 'project' }>`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.textRegular};
  flex-shrink: 0;
  margin-left: 12px;
`;

export default function LearningRecommendationsCard({
  items,
}: LearningRecommendationsCardProps) {
  return (
    <CardWrapper>
      {items.map((item, index) => {
        const displayTitle = item.title || item.slug;
        const countText = item.type === 'module' ? '3 lessons' : '4 exercises';

        return (
          <PlanItemBox key={item.slug || index} type={item.type}>
            <ItemIcon type={item.type}>
              {item.iconUrl ? (
                <img src={item.iconUrl} alt={`${item.type} icon`} />
              ) : (
                <i
                  className={
                    item.type === 'module'
                      ? 'fa-solid fa-book-open'
                      : 'fa-solid fa-rocket'
                  }
                />
              )}
            </ItemIcon>
            <ItemTitle>{displayTitle}</ItemTitle>
            <ItemCount type={item.type}>{countText}</ItemCount>
          </PlanItemBox>
        );
      })}
    </CardWrapper>
  );
}
